import React from "react";
import styled from "styled-components";
import { Head, router, usePage } from "@inertiajs/react";
import { GlobalStyle } from "../../constants/global-style";
import ProvideLoggedInUser from "../../providers/ProvideLoggedInUser";
import SectionErrorBoundary from "../../homes/SectionErrorBoundary";
import SertifikasiApp from "../../sertifikasi/App";
import Breadcrumbs from "../../sertifikasi/Breadcrumbs";
import LessonHero from "../../sertifikasi/sections/LessonHero";
import LessonRecording from "../../sertifikasi/sections/LessonRecording";
import LessonMaterials from "../../sertifikasi/sections/LessonMaterials";
import LessonComments from "../../sertifikasi/sections/LessonComments";
import LessonSiblings from "../../sertifikasi/sections/LessonSiblings";
import RelatedCourses from "../../sertifikasi/sections/RelatedCourses";
import { outerContainerCss } from "../../constants/styles";

export default function Lesson() {
  const { theme = "light", lesson = {} } = usePage().props;
  const title = lesson.name || "Materi";
  const description = (lesson.description || title).slice(0, 160);

  const [autoAdvance, setAutoAdvance] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = window.localStorage.getItem(LessonSiblings.STORAGE_KEY);
      if (saved === "1") setAutoAdvance(true);
    } catch (_) { /* harmless */ }
  }, []);

  const handleToggleAutoAdvance = (next) => {
    setAutoAdvance(!!next);
    try {
      window.localStorage.setItem(LessonSiblings.STORAGE_KEY, next ? "1" : "0");
    } catch (_) { /* harmless */ }
  };

  const handleLessonEnded = React.useCallback(() => {
    if (!autoAdvance) return;
    if (!lesson.next_lesson?.detail_url) return;
    router.visit(lesson.next_lesson.detail_url);
  }, [autoAdvance, lesson.next_lesson?.detail_url]);

  // Server-authoritative progress shared across the in-player status
  // chip, the LessonHero chip below the title, and the watched-bar
  // overlay inside the player. Seeded from Inertia props on first
  // mount and re-seeded on lesson navigation; live updates come
  // exclusively from `handleTickResponse` below — which mirrors the
  // values shipped by the 5-second tick endpoint (computed by the
  // SAME backend methods the LessonDetailPresenter uses on initial
  // render). No parallel client-side progress math.
  const [livePercent, setLivePercent] = React.useState(
    typeof lesson.lesson_progress_percent === "number"
      ? lesson.lesson_progress_percent
      : 0,
  );
  const [liveWatchedTimes, setLiveWatchedTimes] = React.useState(
    Array.isArray(lesson.video_recording?.watched_times)
      ? lesson.video_recording.watched_times
      : [],
  );

  // Re-seed when Inertia ships fresh props (sibling navigation, full
  // page reload) so the chip doesn't flash a stale value before the
  // first tick lands.
  React.useEffect(() => {
    if (typeof lesson.lesson_progress_percent === "number") {
      setLivePercent(lesson.lesson_progress_percent);
    }
    setLiveWatchedTimes(
      Array.isArray(lesson.video_recording?.watched_times)
        ? lesson.video_recording.watched_times
        : [],
    );
  }, [lesson.id]);

  // Apply each tick's response. Floors `percent` against the previous
  // value so a momentary stale read between worker writes can never
  // visually regress the chip — matches the legacy never-regress UX.
  const handleTickResponse = React.useCallback(({ percent, watched_times }) => {
    if (typeof percent === "number" && percent >= 0) {
      setLivePercent((prev) => (percent >= prev ? percent : prev));
    }
    if (Array.isArray(watched_times) && watched_times.length > 0) {
      setLiveWatchedTimes(watched_times);
    }
  }, []);

  // Coerce the lesson hero chip so it follows the lifted live value
  // rather than the (potentially stale) initial server snapshot.
  const heroLesson = React.useMemo(
    () => ({ ...lesson, lesson_progress_percent: livePercent }),
    [lesson, livePercent],
  );

  return (
    <>
      <Head>
        <title>{`${title} — Taxsam Learning Center`}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <GlobalStyle />
      <ProvideLoggedInUser>
        <SertifikasiApp theme={theme}>
          <Breadcrumbs items={lesson.breadcrumbs || []} sticky />
          <ContentSection>
            <ContentInner>
              <Main>
                <SectionErrorBoundary name="LessonRecording">
                  <PlayerFrame>
                    <LessonRecording
                      recording={lesson.video_recording}
                      canWatch={lesson.can_watch !== false}
                      membershipsUrl={lesson.memberships_url || "/memberships"}
                      onLessonEnded={handleLessonEnded}
                      lessonProgressPercent={livePercent}
                      watchedTimes={liveWatchedTimes}
                      onTickResponse={handleTickResponse}
                    />
                  </PlayerFrame>
                </SectionErrorBoundary>
                <SectionErrorBoundary name="LessonHero">
                  <LessonHero
                    lesson={heroLesson}
                    courseSlug={lesson.course?.slug}
                    curriculumSlug={lesson.curriculum?.slug}
                    courseName={lesson.course?.name}
                    curriculumName={lesson.curriculum?.name}
                  />
                </SectionErrorBoundary>
                <SectionErrorBoundary name="LessonMaterials">
                  <LessonMaterials moduleUrl={lesson.module_url} workbookUrl={lesson.workbook_url} />
                </SectionErrorBoundary>
                {lesson.description && (
                  <SectionErrorBoundary name="LessonDescription">
                    <Description>{lesson.description}</Description>
                  </SectionErrorBoundary>
                )}
                <SectionErrorBoundary name="LessonComments">
                  <LessonComments lessonResourceId={lesson.id} comments={lesson.comments} />
                </SectionErrorBoundary>
              </Main>
              <Side>
                <SectionErrorBoundary name="LessonSiblings">
                  <LessonSiblings
                    siblings={lesson.siblings || []}
                    nextLesson={lesson.next_lesson}
                    autoAdvance={autoAdvance}
                    onToggleAutoAdvance={handleToggleAutoAdvance}
                  />
                </SectionErrorBoundary>
              </Side>
            </ContentInner>
          </ContentSection>
          <SectionErrorBoundary name="LessonRelated">
            <RelatedCourses courses={lesson.related_courses || []} />
          </SectionErrorBoundary>
        </SertifikasiApp>
      </ProvideLoggedInUser>
    </>
  );
}

const ContentSection = styled.section`
  background: #ffffff;
  padding: 28px 0 56px;
  [data-theme="dark"] & { background: #0f172a; }

  @media (max-width: 1023px) {
    padding: 16px 0 32px;
  }
`;

const ContentInner = styled.div`
  ${outerContainerCss}
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 36px;
  align-items: start;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Main = styled.div`
  display: flex; flex-direction: column; gap: 24px; min-width: 0;
`;

const Side = styled.aside`
  display: flex; flex-direction: column; gap: 18px;
  position: sticky;
  top: 16px;

  @media (max-width: 1023px) {
    position: static;
    gap: 14px;
  }
`;

const PlayerFrame = styled.div`
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.08);
  background: #000;
`;

const Description = styled.div`
  font-size: 16px;
  line-height: 1.75;
  color: #374151;
  white-space: pre-wrap;

  [data-theme="dark"] & { color: #d1d5db; }
`;
